import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
  LuCalendarRange, LuCheck, LuClock, LuFileSpreadsheet, LuLoaderCircle, LuPlus, LuTrash2, LuTrendingDown, LuTrendingUp, LuWallet, LuX,
} from 'react-icons/lu';
import { db } from '../../../config/firebaseConfig';
import { TextInput } from '../../../components/form/FormControls';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import AddBudgetModal from './AddBudgetModal';
import useProjectFinanceText from './projectFinanceText';

/**
 * Amount as a number: numbers as they are, "1.234,56" (Turkish) and
 * "1234.56" (what the budget form stores) both read correctly.
 */
export const parseAmount = (amount) => {
  if (typeof amount === 'number') return amount;
  if (typeof amount !== 'string' || !amount.trim()) return 0;
  const text = amount.trim();
  const number = text.includes(',') ? Number(text.replace(/\./g, '').replace(',', '.')) : parseFloat(text);
  return Number.isFinite(number) ? number : 0;
};

const trNumber = (value, signed = false) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2, ...(signed ? { signDisplay: 'always' } : {}) }).format(value);
const trDate = (dateString) => (dateString ? new Date(dateString).toLocaleDateString('tr-TR') : '-');

const TH = 'tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-500 dark:tw-text-slate-400';
const TD = 'tw-px-3 tw-py-2.5 tw-text-sm';

/**
 * Proje Bütçe Yönetimi: the project's income (none recorded yet, as before)
 * and expenses — its employees' salary payments and other payments, and its
 * Budgets records — with totals, a date range, the detailed Excel report and
 * new budgets. Only Budgets records can change status or be deleted (the
 * salary / payment rows are not Budgets documents).
 */
const BudgetModal = ({ project, onClose }) => {
  const { language, label, formatMoney, formatDate } = useProjectFinanceText();
  const [budgets, setBudgets] = useState([]);
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isAddBudgetModalOpen, setIsAddBudgetModalOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [statusBusy, setStatusBusy] = useState(null);

  const projectName = project.name || project.titleShort;

  const fetchProjectExpenses = useCallback(async () => {
    if (!project?.id) return [];
    try {
      const allExpenses = [];

      // 1. Employees assigned to this project
      const projectDoc = await getDoc(doc(db, 'Projects', project.id));
      const projectData = projectDoc.data();
      const projectEmployees = projectData?.employees || [];

      // 2. Their payments
      for (const employee of projectEmployees) {
        // 2.1 Salary payments
        const salarySnapshot = await getDocs(query(collection(db, 'Salaries'), where('employeeId', '==', employee.id)));
        salarySnapshot.docs.forEach((salaryDoc) => {
          const salaryData = salaryDoc.data();
          if (salaryData.payments) {
            Object.entries(salaryData.payments).forEach(([yearMonth, payment]) => {
              allExpenses.push({
                id: `${salaryDoc.id}-${yearMonth}`,
                source: 'salary',
                employeeName: `${employee.name} ${employee.surname}`,
                paymentDate: payment.paidDate,
                type: 'Gider',
                category: 'Maaş Ödemesi',
                amount: payment.amount,
                description: `${yearMonth} dönemi maaş ödemesi`,
                paymentStatus: 'paid', // Maaş ödemeleri ödenmiş kabul edilir
              });
            });
          }
        });

        // 2.2 Other payments on this project
        const paymentsSnapshot = await getDocs(query(collection(db, 'Payments'), where('employeeId', '==', employee.id), where('projectId', '==', project.id)));
        paymentsSnapshot.docs.forEach((paymentDoc) => {
          const paymentData = paymentDoc.data();
          allExpenses.push({
            id: paymentDoc.id,
            source: 'payment',
            employeeName: `${employee.name} ${employee.surname}`,
            paymentDate: paymentData.paymentDate,
            type: 'Gider',
            category: paymentData.paymentType || 'Diğer Ödeme',
            amount: paymentData.paidAmount,
            description: paymentData.description,
            paymentStatus: paymentData.paymentStatus || 'paid',
          });
        });
      }

      // 3. Budgets of this project
      const budgetsSnapshot = await getDocs(query(collection(db, 'Budgets'), where('projectId', '==', project.id)));
      const employeeDetailsMap = new Map();
      for (const docSnap of budgetsSnapshot.docs) {
        const budgetData = docSnap.data();
        let employeeName = budgetData.BütçeSahibi || 'Belirsiz';

        // BütçeSahibi may be an employee document id
        if (/^[a-zA-Z0-9]{20,}$/.test(employeeName)) {
          try {
            if (!employeeDetailsMap.has(employeeName)) {
              const employeeDoc = await getDoc(doc(db, 'Employees', employeeName));
              if (employeeDoc.exists()) {
                const employeeData = employeeDoc.data();
                employeeDetailsMap.set(employeeName, `${employeeData.eName} ${employeeData.eSurname}`);
              }
            }
            employeeName = employeeDetailsMap.get(employeeName) || budgetData.BütçeSahibi;
          } catch (error) {
            console.error('Error fetching employee details:', error);
          }
        }

        allExpenses.push({
          id: docSnap.id,
          source: 'budget',
          employeeName,
          paymentDate: budgetData.BütçeTarihi,
          type: 'Gider',
          category: budgetData.BütçeKategorisi || 'Bütçe',
          amount: budgetData.BütçeMiktarı,
          description: budgetData.BütçeAdı,
          paymentStatus: budgetData.paymentStatus || 'unpaid', // Varsayılan: ödenmedi
        });
      }

      // 4. Newest first
      allExpenses.sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
      return allExpenses;
    } catch (error) {
      console.error('Error fetching project expenses:', error);
      toast.error(label('budget.expenseError'));
      return [];
    }
  }, [project, label]);

  // Gelir kayıtları henüz tutulmuyor (eski sayfadaki gibi boş)
  const fetchProjectIncome = useCallback(async () => [], []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const expenses = await fetchProjectExpenses();
      const incomes = await fetchProjectIncome();
      setBudgets(expenses);
      setIncomeRecords(incomes);
    } catch (error) {
      console.error('Error loading budget data:', error);
      toast.error(label('budget.loadError'));
    } finally {
      setLoading(false);
    }
  }, [fetchProjectExpenses, fetchProjectIncome, label]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totals = useMemo(() => {
    const income = incomeRecords.reduce((sum, record) => sum + parseAmount(record.amount), 0);
    const expense = budgets.reduce((sum, record) => sum + parseAmount(record.amount), 0);
    return { income, expense, balance: income - expense };
  }, [incomeRecords, budgets]);

  // Tarih aralığı: girilen sınırlar uygulanır (bitiş günü dahil)
  const filterByDateRange = useCallback((records) => {
    if (!dateFrom && !dateTo) return records;
    const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;
    return records.filter((record) => {
      const recordDate = new Date(record.paymentDate || record.date);
      if (from && recordDate < from) return false;
      if (to && recordDate > to) return false;
      return true;
    });
  }, [dateFrom, dateTo]);

  const visibleIncomes = filterByDateRange(incomeRecords);
  const visibleExpenses = filterByDateRange(budgets);

  const handlePaymentStatusChange = async (budget) => {
    const newStatus = budget.paymentStatus === 'paid' ? 'unpaid' : 'paid';
    setStatusBusy(budget.id);
    try {
      await updateDoc(doc(db, 'Budgets', budget.id), {
        paymentStatus: newStatus,
        lastUpdated: new Date().toISOString(),
      });
      setBudgets((prev) => prev.map((item) => (item.id === budget.id ? { ...item, paymentStatus: newStatus } : item)));
      toast.success(newStatus === 'paid' ? label('budget.markedPaid') : label('budget.markedUnpaid'));
    } catch (error) {
      console.error('Ödeme durumu güncellenirken hata:', error);
      toast.error(label('budget.statusError'));
    } finally {
      setStatusBusy(null);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'Budgets', toDelete.id));
      setBudgets((prev) => prev.filter((item) => item.id !== toDelete.id));
      toast.success(label('budget.deleted'));
      setToDelete(null);
      await loadData(); // totals
    } catch (error) {
      console.error('Gider silinirken hata:', error);
      toast.error(label('budget.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadExcel = () => {
    try {
      const filteredIncomes = visibleIncomes;
      const filteredExpenses = visibleExpenses;
      const workbook = XLSX.utils.book_new();
      const allData = [];

      // Title and project
      allData.push([`PROJE BÜTÇE RAPORU - ${projectName || 'Belirsiz'}`]);
      allData.push([`Rapor Tarihi: ${new Date().toLocaleDateString('tr-TR')}`]);
      if (dateFrom || dateTo) {
        allData.push([`Tarih Aralığı: ${dateFrom ? trDate(dateFrom) : ''} - ${dateTo ? trDate(dateTo) : ''}`]);
      } else {
        allData.push(['Tarih Aralığı: Tüm Kayıtlar']);
      }
      allData.push([]);

      // Summary
      allData.push(['ÖZET BİLGİLER']);
      allData.push(['Toplam Gelir', 'Toplam Gider', 'Net Bakiye']);
      allData.push([trNumber(totals.income), trNumber(totals.expense), trNumber(totals.balance, true)]);
      allData.push([]);
      allData.push([]);

      // Incomes
      allData.push(['GELİRLER']);
      if (filteredIncomes.length > 0) {
        allData.push(['Tarih', 'Açıklama', 'Kategori', 'Tutar (TL)', '', '', '', '', '', '']);
        filteredIncomes.forEach((income) => {
          allData.push([trDate(income.date), income.description || 'Açıklama yok', income.category || 'Kategori yok', parseAmount(income.amount), '', '', '', '', '', '']);
        });
        const totalIncomeAmount = filteredIncomes.reduce((sum, income) => sum + parseAmount(income.amount), 0);
        allData.push(['', '', 'TOPLAM GELİR:', totalIncomeAmount, '', '', '', '', '', '']);
      } else {
        allData.push(['Gelir kaydı bulunamadı']);
      }
      allData.push([]);
      allData.push([]);

      // Expenses
      allData.push(['GİDERLER']);
      if (filteredExpenses.length > 0) {
        allData.push(['Tarih', 'Çalışan', 'Açıklama', 'Kategori', 'Tutar (TL)', 'Durum', '', '', '', '']);
        filteredExpenses.forEach((expense) => {
          allData.push([
            trDate(expense.paymentDate),
            expense.employeeName || 'Belirsiz',
            expense.description || 'Açıklama yok',
            expense.category || 'Kategori yok',
            parseAmount(expense.amount),
            expense.paymentStatus === 'paid' ? 'Ödendi' : 'Ödenmedi',
            '', '', '', '',
          ]);
        });
        const totalExpenseAmount = filteredExpenses.reduce((sum, expense) => sum + parseAmount(expense.amount), 0);
        allData.push(['', '', '', 'TOPLAM GİDER:', totalExpenseAmount, '', '', '', '', '']);
      } else {
        allData.push(['Gider kaydı bulunamadı']);
      }
      allData.push([]);
      allData.push([]);

      // Per category
      allData.push(['DETAYLI ANALİZ']);
      allData.push(['Kategori', 'Gelir Toplamı', 'Gider Toplamı', 'Net Fark', 'Yüzde', 'Durum', 'Açıklama', '', '', '']);
      const categoryAnalysis = {};
      filteredIncomes.forEach((income) => {
        const category = income.category || 'Diğer';
        if (!categoryAnalysis[category]) categoryAnalysis[category] = { income: 0, expense: 0 };
        categoryAnalysis[category].income += parseAmount(income.amount);
      });
      filteredExpenses.forEach((expense) => {
        const category = expense.category || 'Diğer';
        if (!categoryAnalysis[category]) categoryAnalysis[category] = { income: 0, expense: 0 };
        categoryAnalysis[category].expense += parseAmount(expense.amount);
      });
      Object.entries(categoryAnalysis).forEach(([category, data]) => {
        const netDiff = data.income - data.expense;
        const totalAmount = data.income + data.expense;
        const percentage = totalAmount > 0 ? ((netDiff / totalAmount) * 100).toFixed(2) : 0;
        const status = netDiff > 0 ? 'Pozitif' : netDiff < 0 ? 'Negatif' : 'Dengeli';
        allData.push([category, data.income, data.expense, netDiff, `${percentage}%`, status, netDiff > 0 ? 'Kar' : netDiff < 0 ? 'Zarar' : 'Dengeli', '', '', '']);
      });

      const worksheet = XLSX.utils.aoa_to_sheet(allData);
      worksheet['!cols'] = [{ wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
      worksheet['!margins'] = { left: 0.7, right: 0.7, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 };
      worksheet['!printSettings'] = { orientation: 'landscape', paperSize: 9, scale: 85, fitToWidth: 1, fitToHeight: 0 };
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Proje Bütçe Raporu');

      const fileName = `${projectName || 'Proje'}_Detayli_Butce_Raporu_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '_')}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      toast.success(label('budget.excelDone'));
    } catch (error) {
      console.error('Excel indirme hatası:', error);
      toast.error(label('budget.excelError'));
    }
  };

  const summary = [
    { key: 'income', value: formatMoney(totals.income), icon: LuTrendingUp, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30' },
    { key: 'expense', value: formatMoney(totals.expense), icon: LuTrendingDown, gradient: 'tw-from-rose-400 tw-to-rose-600 tw-shadow-rose-500/30' },
    { key: 'balance', value: `${totals.balance > 0 ? '+' : ''}${formatMoney(totals.balance)}`, icon: LuWallet, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30' },
  ];

  const statusButton = (budget) => {
    const paid = budget.paymentStatus === 'paid';
    const Icon = paid ? LuCheck : LuClock;
    const tone = paid
      ? 'tw-border-emerald-500/25 tw-bg-emerald-500/10 tw-text-emerald-700 dark:tw-text-emerald-300'
      : 'tw-border-amber-500/25 tw-bg-amber-400/15 tw-text-amber-800 dark:tw-text-amber-200';
    const content = (
      <>
        {statusBusy === budget.id ? <LuLoaderCircle aria-hidden="true" className="tw-h-3 tw-w-3 tw-animate-spin" /> : <Icon aria-hidden="true" className="tw-h-3 tw-w-3" />}
        {paid ? label('budget.paid') : label('budget.unpaid')}
      </>
    );
    if (budget.source !== 'budget') {
      return <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${tone}`}>{content}</span>;
    }
    return (
      <button
        type="button"
        onClick={() => handlePaymentStatusChange(budget)}
        disabled={statusBusy === budget.id}
        title={label('budget.toggleStatus', { status: paid ? label('budget.unpaid') : label('budget.paid') })}
        className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-transition-transform hover:tw-scale-[1.03] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/40 disabled:tw-opacity-60 ${tone}`}
      >
        {content}
      </button>
    );
  };

  const deleteButton = (budget) => (budget.source === 'budget' ? (
    <button
      type="button"
      onClick={() => setToDelete(budget)}
      title={label('budget.delete')}
      aria-label={label('budget.delete')}
      className="tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-rounded-full tw-text-rose-600 hover:tw-bg-rose-500/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-rose-500/40 dark:tw-text-rose-300"
    >
      <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
    </button>
  ) : <span className="tw-block tw-w-8" />);

  const loadingRow = (
    <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-8 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
      <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
      {label('common.loading')}
    </p>
  );

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        size="xl"
        title={label('budget.title')}
        subtitle={projectName}
        closeLabel={label('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30" size="xs">
            <LuWallet aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={<Button variant="ghost" onClick={onClose}>{label('common.close')}</Button>}
      >
        <div className="tw-space-y-4">
          <div className="tw-grid tw-gap-2.5 sm:tw-grid-cols-3">
            {summary.map(({ key, value, icon: Icon, gradient }) => (
              <div key={key} className="tw-flex tw-min-w-0 tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                <GlassIcon gradient={gradient} size="xs"><Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /></GlassIcon>
                <div className="tw-min-w-0">
                  <p className="tw-truncate tw-text-xs tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{label(`budget.summary.${key}`)}</p>
                  <p className="tw-truncate tw-text-lg tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{loading ? '…' : value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-2">
            <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-wrap tw-items-end tw-gap-2">
              <label className="tw-min-w-[9.5rem] tw-flex-1 sm:tw-flex-none">
                <span className="tw-mb-1 tw-flex tw-items-center tw-gap-1 tw-px-0.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
                  <LuCalendarRange aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                  {label('budget.from')}
                </span>
                <TextInput type="date" value={dateFrom} max={dateTo || undefined} onChange={(event) => setDateFrom(event.target.value)} />
              </label>
              <label className="tw-min-w-[9.5rem] tw-flex-1 sm:tw-flex-none">
                <span className="tw-mb-1 tw-block tw-px-0.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('budget.to')}</span>
                <TextInput type="date" value={dateTo} min={dateFrom || undefined} onChange={(event) => setDateTo(event.target.value)} />
              </label>
              {(dateFrom || dateTo) && (
                <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }} className="tw-mb-1">
                  <LuX aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                  {label('budget.clearRange')}
                </Button>
              )}
            </div>
            <div className="tw-flex tw-gap-2 max-sm:tw-w-full">
              <Button onClick={handleDownloadExcel} className="max-sm:tw-flex-1">
                <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
                {label('budget.excel')}
              </Button>
              <Button variant="primary" onClick={() => setIsAddBudgetModalOpen(true)} className="max-sm:tw-flex-1">
                <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
                {label('budget.add')}
              </Button>
            </div>
          </div>

          {/* Gelirler */}
          <section aria-label={label('budget.incomes')} className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200/80 dark:tw-border-white/10">
            <h3 className="tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-emerald-500/20 tw-bg-emerald-500/[0.07] tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-emerald-800 dark:tw-text-emerald-200">
              <LuTrendingUp aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('budget.incomes')}
            </h3>
            {loading ? loadingRow : visibleIncomes.length === 0 ? (
              <p className="tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('budget.noIncome')}</p>
            ) : (
              <div className="tw-overflow-x-auto glass-scrollbar">
                <table className="tw-w-full tw-border-collapse">
                  <thead><tr>
                    <th className={TH}>{label('budget.columns.date')}</th>
                    <th className={TH}>{label('budget.columns.description')}</th>
                    <th className={TH}>{label('budget.columns.category')}</th>
                    <th className={`${TH} tw-text-right`}>{label('budget.columns.amount')}</th>
                  </tr></thead>
                  <tbody>
                    {visibleIncomes.map((income, index) => (
                      <tr key={income.id || index} className="tw-border-t tw-border-slate-200/70 dark:tw-border-white/[0.06]">
                        <td className={`${TD} tw-whitespace-nowrap`}>{formatDate(income.date) || '-'}</td>
                        <td className={TD}>{income.description || label('budget.noDescription')}</td>
                        <td className={TD}>{income.category || label('budget.noCategory')}</td>
                        <td className={`${TD} tw-whitespace-nowrap tw-text-right tw-font-semibold tw-tabular-nums`}>{formatMoney(parseAmount(income.amount))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Giderler */}
          <section aria-label={label('budget.expenses')} className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200/80 dark:tw-border-white/10">
            <h3 className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-b tw-border-rose-500/20 tw-bg-rose-500/[0.06] tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-rose-800 dark:tw-text-rose-200">
              <span className="tw-flex tw-items-center tw-gap-2">
                <LuTrendingDown aria-hidden="true" className="tw-h-4 tw-w-4" />
                {label('budget.expenses')}
              </span>
              {!loading && <span className="tw-text-xs tw-font-medium tw-opacity-80">{label('budget.count', { count: visibleExpenses.length })}</span>}
            </h3>
            {loading ? loadingRow : visibleExpenses.length === 0 ? (
              <p className="tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('budget.noExpense')}</p>
            ) : (
              <>
                <ul className="tw-divide-y tw-divide-slate-200/70 md:tw-hidden dark:tw-divide-white/[0.06]">
                  {visibleExpenses.map((budget, index) => (
                    <li key={budget.id || index} className="tw-flex tw-items-start tw-gap-3 tw-px-4 tw-py-3">
                      <div className="tw-min-w-0 tw-flex-1">
                        <p className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{budget.description || label('budget.noDescription')}</p>
                        <p className="tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                          {[formatDate(budget.paymentDate), budget.employeeName || label('budget.unknown'), budget.category].filter(Boolean).join(' · ')}
                        </p>
                        <div className="tw-mt-1.5">{statusButton(budget)}</div>
                      </div>
                      <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-end tw-gap-1">
                        <span className="tw-text-sm tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{formatMoney(parseAmount(budget.amount))}</span>
                        {deleteButton(budget)}
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="tw-hidden tw-overflow-x-auto md:tw-block glass-scrollbar">
                  <table className="tw-w-full tw-border-collapse">
                    <thead><tr>
                      <th className={TH}>{label('budget.columns.date')}</th>
                      <th className={TH}>{label('budget.columns.employee')}</th>
                      <th className={TH}>{label('budget.columns.description')}</th>
                      <th className={TH}>{label('budget.columns.category')}</th>
                      <th className={`${TH} tw-text-right`}>{label('budget.columns.amount')}</th>
                      <th className={TH}>{label('budget.columns.status')}</th>
                      <th className={TH}><span className="tw-sr-only">{label('budget.columns.actions')}</span></th>
                    </tr></thead>
                    <tbody>
                      {visibleExpenses.map((budget, index) => (
                        <tr key={budget.id || index} className="tw-border-t tw-border-slate-200/70 dark:tw-border-white/[0.06]">
                          <td className={`${TD} tw-whitespace-nowrap tw-text-slate-700 dark:tw-text-slate-200`}>{formatDate(budget.paymentDate) || '-'}</td>
                          <td className={`${TD} tw-whitespace-nowrap tw-text-slate-700 dark:tw-text-slate-200`}>{budget.employeeName || label('budget.unknown')}</td>
                          <td className={`${TD} tw-whitespace-normal tw-font-medium tw-text-slate-900 dark:tw-text-white`}>{budget.description || label('budget.noDescription')}</td>
                          <td className={`${TD} tw-whitespace-nowrap tw-text-slate-600 dark:tw-text-slate-300`}>{budget.category || label('budget.noCategory')}</td>
                          <td className={`${TD} tw-whitespace-nowrap tw-text-right tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white`}>{formatMoney(parseAmount(budget.amount))}</td>
                          <td className={`${TD} tw-whitespace-nowrap`}>{statusButton(budget)}</td>
                          <td className={`${TD} tw-w-px`}>{deleteButton(budget)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
          <p className="tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{label('budget.note')}</p>
        </div>
      </Modal>

      {isAddBudgetModalOpen && (
        <AddBudgetModal
          isOpen={isAddBudgetModalOpen}
          onClose={() => setIsAddBudgetModalOpen(false)}
          onSubmit={() => loadData()}
          projectId={project.id}
          projectName={projectName}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(toDelete)}
        title={label('budget.deleteTitle')}
        message={label('budget.deleteMessage', { name: toDelete?.description || label('budget.thisExpense') })}
        confirmLabel={label('budget.delete')}
        cancelLabel={label('common.cancel')}
        tone="danger"
        icon={LuTrash2}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => !deleting && setToDelete(null)}
      />
    </>
  );
};

BudgetModal.propTypes = {
  project: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default BudgetModal;
